import type { TFunction } from 'i18next';
import { localeTag } from '../i18n/index.js';

/**
 * Форматирование дат и чисел через Intl — под выбранный язык. Понедельник как начало недели
 * при этом остаётся неизменным: это требование продукта, а не локали.
 */

/** Дата в таблице: 15.08 — год не показываем, если он текущий. */
export function formatShortDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return '—';

  const sameYear = date.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat(localeTag(), {
    day: '2-digit',
    month: '2-digit',
    ...(sameYear ? {} : { year: '2-digit' }),
  }).format(date);
}

/**
 * Момент обновления: сегодняшние правки показываются временем, вчерашние — словом,
 * остальное — датой. Так колонка «Обновлено» читается без вычислений в голове.
 */
export function formatUpdated(value: string | null, t: TFunction): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday.getTime() - 24 * 3_600_000);

  if (date >= startOfToday) {
    return new Intl.DateTimeFormat(localeTag(), { hour: '2-digit', minute: '2-digit' }).format(date);
  }
  if (date >= startOfYesterday) return t('time.yesterday');
  return formatShortDate(value);
}

/** «Синхронизировано 2 минуты назад» — подпись под списком разделов. */
export function formatRelative(value: string | null, t: TFunction): string {
  if (!value) return t('sections.never');

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t('sections.never');

  const minutes = Math.floor((Date.now() - date.getTime()) / 60_000);
  if (minutes < 1) return t('time.justNow');
  if (minutes < 60) return t('time.minutesAgo', { count: minutes });

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('time.hoursAgo', { count: hours });

  return formatShortDate(value);
}

/**
 * Размер файла. До мегабайта — целые килобайты, дальше — десятые доли мегабайта: под именем
 * вложения нужен порядок величины, а не точные байты.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;

  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${new Intl.NumberFormat(localeTag(), { maximumFractionDigits: 0 }).format(kb)} KB`;
  }

  const mb = kb / 1024;
  return `${new Intl.NumberFormat(localeTag(), { maximumFractionDigits: 1 }).format(mb)} MB`;
}

/** Инициалы для аватарки-заглушки: две первые буквы имени и фамилии. */
export function initialsOf(name: string | null | undefined): string {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/u).slice(0, 2);
  return parts.map((part) => part.charAt(0).toUpperCase()).join('') || '—';
}

/** Сегодняшняя дата в виде YYYY-MM-DD по часам пользователя, а не по UTC. */
export function todayIso(): string {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate(),
  ).padStart(2, '0')}`;
}

/** Просрочен ли срок: сравнение календарных дней, без времени и часовых поясов. */
export function isOverdue(duedate: string | null): boolean {
  if (!duedate) return false;
  return duedate < todayIso();
}
